import { supabase } from '../lib/supabase';

export interface DiscountCodeValidation {
  isValid: boolean;
  code?: {
    id: string;
    code: string;
    description?: string;
    type: 'percentage' | 'fixed';
    value: number;
    min_order_amount?: number;
    max_uses?: number;
    used_count: number;
    expires_at?: string;
    is_active: boolean;
  };
  error?: string;
  discountAmount?: number;
}

export class DiscountCodeService {
  /**
   * Validate a discount code for a specific business and order amount
   */
  static async validateDiscountCode(
    businessId: string,
    code: string,
    orderAmount: number
  ): Promise<DiscountCodeValidation> {
    try {
      // Fetch the discount code
      const { data: discountCode, error } = await supabase
        .from('discount_codes')
        .select('*')
        .eq('business_id', businessId)
        .eq('code', code.toUpperCase().trim())
        .single();

      if (error || !discountCode) {
        return {
          isValid: false,
          error: 'Invalid discount code'
        };
      }

      // Check if code is active
      if (!discountCode.is_active) {
        return {
          isValid: false,
          error: 'This discount code is no longer active'
        };
      }

      // Check if code has expired
      if (discountCode.expires_at && new Date(discountCode.expires_at) < new Date()) {
        return {
          isValid: false,
          error: 'This discount code has expired'
        };
      }

      // Check usage limits
      if (discountCode.max_uses && discountCode.used_count >= discountCode.max_uses) {
        return {
          isValid: false,
          error: 'This discount code has reached its usage limit'
        };
      }

      // Check minimum order amount
      if (discountCode.min_order_amount && orderAmount < discountCode.min_order_amount) {
        return {
          isValid: false,
          error: `Minimum order amount of $${discountCode.min_order_amount} required for this discount`
        };
      }

      // Calculate discount amount
      const discountAmount = this.calculateDiscountAmount(discountCode, orderAmount);

      return {
        isValid: true,
        code: discountCode,
        discountAmount
      };
    } catch (error) {
      console.error('Error validating discount code:', error);
      return {
        isValid: false,
        error: 'Failed to validate discount code. Please try again.'
      };
    }
  }

  /**
   * Apply a discount code to a booking
   */
  static async applyDiscountCode(
    bookingId: string,
    businessId: string,
    code: string,
    orderAmount: number
  ): Promise<{ success: boolean; error?: string; discount?: any }> {
    try {
      // First validate the code
      const validation = await this.validateDiscountCode(businessId, code, orderAmount);
      
      if (!validation.isValid || !validation.code) {
        return {
          success: false,
          error: validation.error
        };
      }

      // Remove any existing discounts from this booking (enforce single discount rule)
      const { error: removeError } = await supabase
        .from('booking_discounts')
        .delete()
        .eq('booking_id', bookingId);

      if (removeError) {
        console.error('Error removing existing discounts:', removeError);
        return {
          success: false,
          error: 'Failed to remove existing discount. Please try again.'
        };
      }

      // Apply the discount
      const { data: discount, error: discountError } = await supabase
        .from('booking_discounts')
        .insert([
          {
            booking_id: bookingId,
            title: validation.code.description || validation.code.code,
            type: validation.code.type,
            amount: validation.code.value,
            discount_code_id: validation.code.id
          }
        ])
        .select()
        .single();

      if (discountError) {
        console.error('Error applying discount:', discountError);
        return {
          success: false,
          error: 'Failed to apply discount code. Please try again.'
        };
      }

      // Update the booking's total amount
      const { data: booking, error: bookingError } = await supabase
        .from('bookings')
        .select('subtotal, tax_amount')
        .eq('id', bookingId)
        .single();

      if (bookingError) {
        console.error('Error fetching booking:', bookingError);
        return {
          success: false,
          error: 'Failed to update booking total'
        };
      }

      // Get product totals from product_booking_sales
      const { data: productSales, error: productError } = await supabase
        .from('product_booking_sales')
        .select('total_price')
        .eq('booking_id', bookingId);

      if (productError) {
        console.error('Error fetching product sales:', productError);
        return {
          success: false,
          error: 'Failed to fetch product details'
        };
      }

      // Calculate product totals (including 5% tax on products)
      const productSubtotal = productSales?.reduce((sum, product) => sum + (product.total_price || 0), 0) || 0;
      const productTaxRate = 0.05; // 5% tax rate - should match BookingProductList
      const productTaxAmount = productSubtotal * productTaxRate;
      const productTotal = productSubtotal + productTaxAmount;

      // Calculate total booking amount including products
      const totalBookingAmount = (booking.subtotal || 0) + (booking.tax_amount || 0) + productTotal;

      const discountAmount = validation.discountAmount || 0;
      const newTotal = Math.round((totalBookingAmount - discountAmount) * 100) / 100;
      
      const { error: updateError } = await supabase
        .from('bookings')
        .update({
          total_amount: newTotal
        })
        .eq('id', bookingId);

      if (updateError) {
        console.error('Error updating booking total:', updateError);
        return {
          success: false,
          error: 'Failed to update booking total'
        };
      }

      // Increment the usage count for the discount code
      const { error: usageError } = await supabase
        .from('discount_codes')
        .update({
          used_count: validation.code.used_count + 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', validation.code.id);

      if (usageError) {
        console.error('Error updating discount code usage:', usageError);
        // Don't fail the whole operation for this
      }

      return {
        success: true,
        discount
      };
    } catch (error) {
      console.error('Error applying discount code:', error);
      return {
        success: false,
        error: 'Failed to apply discount code. Please try again.'
      };
    }
  }

  /**
   * Calculate the discount amount for a given discount code and order amount
   */
  private static calculateDiscountAmount(
    discountCode: { type: 'percentage' | 'fixed'; value: number },
    orderAmount: number
  ): number {
    if (discountCode.type === 'percentage') {
      return Math.round((orderAmount * discountCode.value / 100) * 100) / 100;
    } else {
      return Math.min(discountCode.value, orderAmount); // Don't exceed order amount
    }
  }

  /**
   * Get all discount codes for a business (for admin use)
   */
  static async getBusinessDiscountCodes(businessId: string) {
    const { data, error } = await supabase
      .from('discount_codes')
      .select('*')
      .eq('business_id', businessId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching discount codes:', error);
      return [];
    }

    return data || [];
  }
}
